import {Component, Input, OnInit} from '@angular/core';
import {ModalController} from '@ionic/angular';
import {Product} from '../../types/models';
import {ThermomixItem, ThermomixMatch} from '../../types/thermomix';
import {ThermomixMatchService} from '../../services/customer/thermomix-match.service';
import {ProductService} from '../../services/customer/product.service';
import {ToastService} from '../../services/core/toast.service';

/** Das Ergebnis, mit dem sich der Dialog schliesst. */
export interface ThermomixMatchModalResult {
  Product?: Product;
  /** Der Eintrag wird uebersprungen */
  Skip?: boolean;
  /** Die Entscheidung soll fuer kuenftige Syncs gespeichert werden */
  Remember?: boolean;
  /** Der gesamte Sync wird abgebrochen */
  Cancel?: boolean;
}

/**
 * Fragt den Kunden, welches Kiwi-Produkt zu einem Eintrag der Thermomix-Einkaufsliste gehoert.
 * Der Dialog erscheint nur, wenn kein Treffer die nötige Uebereinstimmung erreicht hat.
 */
@Component({
  selector: 'app-thermomix-match-modal',
  templateUrl: './thermomix-match-modal.page.html',
  styleUrls: ['./thermomix-match-modal.page.scss'],
})
export class ThermomixMatchModalPage implements OnInit {

  @Input() Item: ThermomixItem;
  @Input() Matches: ThermomixMatch[] = [];
  /** Position des Eintrags im Durchlauf, 1-basiert */
  @Input() Index: number = 1;
  @Input() Total: number = 1;

  searchInput = '';
  searchLoading = false;
  remember = true;

  private searchTimer: any;

  constructor(
    private modalController: ModalController,
    private matchService: ThermomixMatchService,
    private productService: ProductService,
    private toastService: ToastService
  ) { }

  ngOnInit() {
    this.searchInput = this.Item?.Name || '';
  }

  getAmount(): string {
    return this.matchService.getAmountForDisplay(this.Item);
  }

  getProductAmount(product: Product): string {
    return this.productService.getAmountForDisplay(product);
  }

  disabled(product: Product): boolean {
    return !this.productService.getProductInStock(product);
  }

  /** Farbe des Prozent-Hinweises: je naeher an einem sicheren Treffer, desto freundlicher. */
  getScoreColor(score: number): string {
    if (score >= ThermomixMatchService.MIN_AUTO_SCORE) {
      return 'color-primary';
    }
    return score >= 60 ? 'color-warning' : 'color-medium';
  }

  onSearchInput(value: string) {
    this.searchInput = value;
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.search(), 400);
  }

  onSearchEnter() {
    clearTimeout(this.searchTimer);
    return this.search();
  }

  async search() {
    const term = (this.searchInput || '').trim();
    if (!term) {
      return;
    }
    this.searchLoading = true;
    this.Matches = await this.matchService.findProducts(term, this.Item?.Name);
    this.searchLoading = false;
  }

  async select(product: Product) {
    if (this.disabled(product)) {
      return this.toastService.presentError('Ausverkauft');
    }
    return this.close({ Product: product, Remember: this.remember });
  }

  async skip() {
    return this.close({ Skip: true, Remember: this.remember });
  }

  async cancel() {
    return this.close({ Cancel: true });
  }

  private async close(result: ThermomixMatchModalResult) {
    clearTimeout(this.searchTimer);
    return this.modalController.dismiss(result);
  }
}
